import React, { useCallback, useMemo } from 'react'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ModalForm from 'core/elements/modal/ModalForm'
import { Route } from 'core/plugins/route'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import CodeBlock from 'core/components/CodeBlock'
import { useSelector } from 'react-redux'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { RootState } from 'app/store'
import { prop } from 'ramda'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Cookies from 'js-cookie'
import { DOMAIN_NAME } from 'app/constants'
import CopyToClipboard from 'core/components/CopyToClipboard'
import Button from 'core/elements/button'
import ExternalLink from 'core/components/ExternalLink'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
}))

interface Props {
  addRoute: Route
}

export default function ImportImageModal({ addRoute }: Props) {
  const { history, match } = useReactRouter()
  const { tab } = match.params

  const classes = useStyles()

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { name },
    features,
    domain,
    activeRegion,
  } = session

  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isSSOUser = session?.userDetails?.federated?.length > 0
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenantName, currentRegion } = prefs
  const fqdn = window.location.host
  const currentDomain = domain?.name || Cookies.get(DOMAIN_NAME) || 'default'

  const stepOffset = isSSOUser ? 1 : 0
  const getStep = useCallback((base: number) => base + stepOffset, [stepOffset])

  const handleClose = () => {
    history.push(routeManager.routes.openstack.images.path({ tab }))
  }

  const OS_DETAILS = `export OS_USERNAME=${name}
export OS_PASSWORD=<your-password>
export OS_AUTH_URL=https://${fqdn}/keystone/v3
export OS_AUTH_TYPE=password
export OS_IDENTITY_API_VERSION=3
export OS_REGION_NAME=${activeRegion || currentRegion}
export OS_USER_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}`

  const OS_DETAILS_V2 = `export OS_AUTH_URL=https://${fqdn}/keystone/v3      
export OS_AUTH_TYPE=token                                                                                                      
export OS_IDENTITY_API_VERSION=3                                                                                                 
export OS_REGION_NAME=${activeRegion ||
    currentRegion}                                                                                                       
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}
export OS_TOKEN=<keystone token from the ~/.pf9/credentials>`

  const osDetails = isSSOUser ? OS_DETAILS_V2 : OS_DETAILS

  return (
    <ModalForm
      route={addRoute}
      title={`Steps to Upload an Image to your Image Library`}
      onClose={handleClose}
      submitting={false}
      footerButtonAlignment="end"
      footer={
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <>
        {isSSOUser && (
          <FormFieldSection title="Generate token using saml2pf9" step={1}>
            <Text variant="body2">
              Follow the instructions given{' '}
              <ExternalLink url="https://platform9.com/docs/private-cloud-director/private-cloud-director/saml2pf9">
                here.
              </ExternalLink>
            </Text>
          </FormFieldSection>
        )}
        <FormFieldSection title="Export the pcdctl RC environment variables" step={getStep(1)}>
          <CopyToClipboard copyText={osDetails}>
            <CodeBlock>{osDetails}</CodeBlock>
          </CopyToClipboard>
        </FormFieldSection>
        <FormFieldSection title="Upload the image using following command" step={getStep(2)}>
          {isVmware ? (
            <CopyToClipboard
              copyText={`glance image-create --name &lt;image-name&gt; --file &lt;image-file-path&gt;
                --disk-format vmdk --container-format bare --property vmware_adaptertype="lsiLogic"
                --property vmware_disktype="sparse" --property vmware_ostype="ubuntu64Guest"
                [--property &lt;key=value&gt;]`}
            >
              <CodeBlock>
                glance image-create --name &lt;image-name&gt; --file &lt;image-file-path&gt;
                --disk-format vmdk --container-format bare --property vmware_adaptertype="lsiLogic"
                --property vmware_disktype="sparse" --property vmware_ostype="ubuntu64Guest"
                [--property &lt;key=value&gt;]
              </CodeBlock>
            </CopyToClipboard>
          ) : (
            <CopyToClipboard
              copyText={`pcdctl image create --insecure --container-format bare --disk-format qcow2 [--public |
              --private] [--protected | --unprotected] [--property <key=value>] --file
              <image-file-path> <image-name>`}
            >
              <CodeBlock>
                pcdctl image create --insecure --container-format bare --disk-format qcow2 [--public
                | --private] [--protected | --unprotected] [--property &lt;key=value&gt;] --file
                &lt;image-file-path&gt; &lt;image-name&gt;
              </CodeBlock>
            </CopyToClipboard>
          )}
        </FormFieldSection>
        <FormFieldSection title="Pre-requisites">
          <Text variant="body2">
            <b>pcdctl</b> needs to be installed for using the above CLI. If not already installed,
            please install it using below command:
          </Text>
          <CopyToClipboard
            copyText={`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
          >
            <CodeBlock>
              {`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
            </CodeBlock>
          </CopyToClipboard>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
